import { Link } from 'react-router-dom';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

export default function SustainabilityPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Sustainability - ignight",
    "url": `${siteUrl}/sustainability`,
    "description": "Learn about ignight's commitment to sustainability: natural ingredients, zero-waste concrete vessels, responsible sourcing, and minimal packaging.",
    "mainEntity": {
      "@type": "Organization",
      "name": "ignight",
      "description": "Luxury concrete candles with pure essential oils",
      "url": siteUrl
    }
  };

  return (
    <>
      <SEOHead
        title="Sustainability - Our Commitment to the Planet | ignight"
        description="Learn about ignight's commitment to sustainability: natural ingredients, zero-waste concrete vessels, responsible sourcing, and minimal packaging."
        keywords="sustainable candles, eco-friendly candles, zero waste candles, natural soy wax, reusable concrete vessels"
        canonical={`${siteUrl}/sustainability`}
        schema={schema}
      />
      <div className="min-h-screen bg-[#0B1B3A]">
        {/* Header */}
        <header className="bg-[#0B1B3A] border-b border-[#162D52]">
          <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
            <Link
              to="/"
              className="text-2xl font-serif text-[#F5F0E8] cursor-pointer"
              style={{ fontFamily: 'Pacifico, serif' }}
            >
              ig-night
            </Link>
            <Link
              to="/"
              className="text-sm text-[#8A9BB5] hover:text-[#F5F0E8] transition-colors uppercase tracking-wider cursor-pointer flex items-center gap-2"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Home
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="relative py-20 sm:py-32 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/038f384461da470384a29e6589284103.jpg"
              alt="Sustainability at ig-night — eco-friendly concrete candles"
              title="ig-night sustainability eco-friendly candles"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3A]/70 via-[#0B1B3A]/50 to-[#0B1B3A]"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <p className="text-xs tracking-widest text-[#D4C5A9] uppercase mb-4">
              Our Commitment
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#F5F0E8] mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Sustainability at ig-night
            </h1>
            <p className="text-sm sm:text-base text-[#8A9BB5] max-w-2xl mx-auto leading-relaxed">
              We believe beautiful things shouldn't cost the earth. Every decision
              we make — from ingredients to packaging — is guided by respect for
              the planet.
            </p>
          </div>
        </section>

        {/* Core Pillars */}
        <section className="py-16 sm:py-24 bg-[#0E2244]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-xs tracking-widest text-[#6B83A8] uppercase mb-3">
                What We Stand For
              </p>
              <h2
                className="text-2xl sm:text-3xl font-serif text-[#F5F0E8]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Our Four Pillars
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                {
                  icon: 'ri-leaf-line',
                  title: 'Natural Ingredients',
                  text: '100% pure essential oils and natural soy wax. No paraffin, no synthetic fragrances, no petroleum-based additives — ever.',
                },
                {
                  icon: 'ri-recycle-line',
                  title: 'Zero-Waste Vessels',
                  text: 'Our concrete vessels are designed to outlast the candle. Reuse them as planters, trinket bowls, or décor pieces — nothing goes to landfill.',
                },
                {
                  icon: 'ri-plant-line',
                  title: 'Responsible Sourcing',
                  text: 'We source essential oils from ethical suppliers who practice sustainable farming and fair trade with local growers.',
                },
                {
                  icon: 'ri-box-3-line',
                  title: 'Minimal Packaging',
                  text: 'Recycled cardboard, soy-based inks, and zero plastic. Our packaging is fully recyclable and compostable.',
                },
              ].map((pillar, i) => (
                <div
                  key={i}
                  className="bg-[#0B1B3A]/50 rounded-lg p-6 sm:p-8 border border-[#162D52] hover:border-[#D4C5A9]/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-full bg-[#162D52] flex items-center justify-center mb-5">
                    <i className={`${pillar.icon} text-[#D4C5A9] text-xl`}></i>
                  </div>
                  <h3
                    className="text-base font-serif text-[#F5F0E8] mb-3"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {pillar.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                    {pillar.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Materials Deep Dive */}
        <section className="py-16 sm:py-24 bg-[#0B1B3A]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-16 items-center">
              <div>
                <p className="text-xs tracking-widest text-[#6B83A8] uppercase mb-3">
                  Materials
                </p>
                <h2
                  className="text-2xl sm:text-3xl font-serif text-[#F5F0E8] mb-6"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  What Goes Into Every Candle
                </h2>
                <div className="space-y-6">
                  {[
                    {
                      label: 'Soy Wax',
                      desc: 'Biodegradable, renewable, and burns cleaner than paraffin. Our soy wax is sourced from non-GMO soybeans grown in the US.',
                    },
                    {
                      label: 'Essential Oils',
                      desc: 'Steam-distilled and cold-pressed from plants, flowers, and herbs. Each blend is formulated in‑house to capture a real destination.',
                    },
                    {
                      label: 'Cotton Wicks',
                      desc: 'Lead‑free, unbleached cotton wicks for a clean, even burn with minimal soot.',
                    },
                    {
                      label: 'Concrete',
                      desc: 'Hand‑cast from a custom mineral blend. Each vessel is unique, durable, and designed for a second life after the candle is finished.',
                    },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-1 rounded-full bg-[#D4C5A9]/40 flex-shrink-0"></div>
                      <div>
                        <h3 className="text-sm font-medium text-[#F5F0E8] mb-1">
                          {item.label}
                        </h3>
                        <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="w-full h-[360px] sm:h-[480px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20raw%20concrete%20candle%20vessel%20with%20natural%20soy%20wax%20and%20dried%20botanicals%20on%20dark%20moody%20background%20with%20warm%20amber%20lighting%20minimalist%20still%20life%20photography%20earth%20tones%20sustainable%20handmade%20craft&width=600&height=480&seq=sust-materials-1&orientation=portrait"
                  alt="ig-night concrete candle materials — soy wax essential oils"
                  title="ig-night sustainable candle materials"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Impact Numbers */}
        <section className="py-16 sm:py-24 bg-[#0E2244]">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-xs tracking-widest text-[#6B83A8] uppercase mb-3">
                Our Impact
              </p>
              <h2
                className="text-2xl sm:text-3xl font-serif text-[#F5F0E8]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Small Batch, Big Difference
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {[
                { number: '0g', label: 'Plastic in Packaging' },
                { number: '100%', label: 'Natural Soy Wax' },
                { number: '100%', label: 'Pure Essential Oils' },
                { number: '∞', label: 'Vessel Reuse Potential' },
              ].map((stat, i) => (
                <div key={i} className="text-center py-6 sm:py-8">
                  <p
                    className="text-3xl sm:text-4xl font-serif text-[#D4C5A9] mb-2"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    {stat.number}
                  </p>
                  <p className="text-xs sm:text-sm text-[#8A9BB5]">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reuse Section */}
        <section className="py-16 sm:py-24 bg-[#0B1B3A]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12 sm:mb-16">
              <p className="text-xs tracking-widest text-[#6B83A8] uppercase mb-3">
                After the Flame
              </p>
              <h2
                className="text-2xl sm:text-3xl font-serif text-[#F5F0E8] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Give Your Vessel a Second Life
              </h2>
              <p className="text-sm text-[#8A9BB5] max-w-xl mx-auto leading-relaxed">
                When the last drop of wax has burned, your concrete vessel is
                just getting started. Here are some of our favourite ways to
                reuse it.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
              {[
                {
                  icon: 'ri-plant-line',
                  title: 'Mini Planter',
                  text: 'Perfect for succulents, cacti, or small herbs. The raw concrete adds a beautiful textural contrast to greenery.',
                  img: 'https://readdy.ai/api/search-image?query=Small%20concrete%20bowl%20used%20as%20a%20succulent%20planter%20with%20tiny%20green%20plant%20inside%20on%20dark%20navy%20background%20minimalist%20still%20life%20warm%20ambient%20lighting%20earth%20tones%20moody%20aesthetic&width=400&height=300&seq=sust-reuse-1&orientation=landscape',
                },
                {
                  icon: 'ri-pencil-ruler-2-line',
                  title: 'Desk Organiser',
                  text: 'Hold pens, brushes, or small tools. The weight of the concrete keeps everything steady and grounded.',
                  img: 'https://readdy.ai/api/search-image?query=Concrete%20bowl%20used%20as%20desk%20organizer%20holding%20pencils%20and%20brushes%20on%20dark%20moody%20background%20minimalist%20workspace%20warm%20lighting%20earth%20tones%20aesthetic%20still%20life%20photography&width=400&height=300&seq=sust-reuse-2&orientation=landscape',
                },
                {
                  icon: 'ri-sparkling-line',
                  title: 'Trinket Bowl',
                  text: 'Rings, keys, coins — a beautiful catch‑all for your bedside table or entryway.',
                  img: 'https://readdy.ai/api/search-image?query=Concrete%20bowl%20used%20as%20trinket%20dish%20holding%20gold%20rings%20and%20jewelry%20on%20dark%20navy%20background%20minimalist%20still%20life%20warm%20amber%20lighting%20earth%20tones%20moody%20aesthetic&width=400&height=300&seq=sust-reuse-3&orientation=landscape',
                },
              ].map((item, i) => (
                <div key={i} className="group">
                  <div className="w-full h-[200px] sm:h-[220px] rounded-lg overflow-hidden mb-4">
                    <img
                      src={item.img}
                      alt={`ig-night concrete vessel reuse — ${item.title}`}
                      title={`ig-night vessel reuse ${item.title}`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${item.icon} text-[#D4C5A9] text-base`}></i>
                    </div>
                    <h3 className="text-sm font-medium text-[#F5F0E8]">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-xs sm:text-sm text-[#8A9BB5] leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Promise Section */}
        <section className="py-16 sm:py-24 bg-[#0E2244]">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="w-14 h-14 rounded-full bg-[#162D52] flex items-center justify-center mx-auto mb-6">
              <i className="ri-heart-line text-[#D4C5A9] text-2xl"></i>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-serif text-[#F5F0E8] mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Our Promise
            </h2>
            <p className="text-sm sm:text-base text-[#8A9BB5] leading-relaxed mb-8">
              We're not perfect — but we're committed to getting better. We
              continuously review our supply chain, test new sustainable
              materials, and look for ways to reduce our footprint. If you have
              ideas or feedback, we'd love to hear from you.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#D4C5A9] text-[#0B1B3A] px-6 py-3 rounded-md text-sm font-medium hover:bg-[#E8DCC4] transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back to Collection
            </Link>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
